import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

export default function MonthlyLeaveConsumptionReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());

  const loadReport = useCallback(async () => {
    if (!selectedDate) return;

    setLoading(true);
    setError('');

    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;

      const response = await api.get(`/leave-requests/admin/monthly-consumption-report?year=${year}&month=${month}`);
      setReportData(response);
    } catch (err) {
      console.error('Failed to load monthly consumption report:', err);
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleRefresh = () => {
    loadReport();
  };

  const handleExportToExcel = async () => {
    if (!selectedDate) {
      setError('Please select a month first.');
      return;
    }

    // Check if there is data to export
    if (!reportData || !reportData.employees || reportData.employees.length === 0) {
      setError('No data available to export. Please load the report first.');
      return;
    }

    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;

      console.log('[Export] Requesting CSV export for:', year, month);

      // NOTE: api.js response interceptor returns response.data directly
      // So when responseType is 'blob', we get the Blob directly, not response.data
      const blob = await api.get(`/leave-requests/admin/monthly-consumption-report/export?year=${year}&month=${month}`, {
        responseType: 'blob'
      });

      console.log('[Export] Blob received:', blob);
      console.log('[Export] Blob type:', typeof blob);
      console.log('[Export] Is Blob?', blob instanceof Blob);

      // Check if blob exists
      if (!blob) {
        console.error('[Export] No blob received');
        setError('No data received from server. Please try again.');
        return;
      }

      // Check if it's a valid Blob
      if (!(blob instanceof Blob)) {
        console.error('[Export] Response is not a Blob:', typeof blob);
        setError('Invalid response format from server. Please try again.');
        return;
      }

      console.log('[Export] Blob size:', blob.size);

      // Check if blob has content
      if (blob.size === 0) {
        console.error('[Export] Empty blob received');
        setError('No data received from server. Please try again.');
        return;
      }

      console.log('[Export] Valid blob received, size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Leave_Consumption_Report_${year}_${month.toString().padStart(2, '0')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Failed to export report:', err);
      if (err.response && err.response.status === 404) {
        setError('Export endpoint not found. Please contact support.');
      } else if (err.response && err.response.status === 500) {
        setError('Server error occurred while exporting. Please try again later.');
      } else {
        setError('Failed to export report. Please check your connection and try again.');
      }
    }
  };

  const formatMonthName = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Monthly Leave Consumption Report
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Report showing consumed working days of leave for previous, current, and next month
        </Typography>

        {/* Controls */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
          <DatePicker
            label="Report Month"
            value={selectedDate}
            onChange={handleDateChange}
            views={['year', 'month']}
            format="yyyy-MM"
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
            disabled={loading || !reportData}
            color="success"
          >
            Export to CSV
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {reportData && !loading && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee Name</strong></TableCell>
                  <TableCell align="center">
                    <strong>Previous Month</strong><br />
                    <Typography variant="caption" color="text.secondary">
                      {formatMonthName(reportData.employees?.[0]?.previousMonth?.month)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Current Month</strong><br />
                    <Typography variant="caption" color="text.secondary">
                      {formatMonthName(reportData.reportMonth)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Next Month</strong><br />
                    <Typography variant="caption" color="text.secondary">
                      {formatMonthName(reportData.employees?.[0]?.nextMonth?.month)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.employees?.map((employee) => (
                  <TableRow key={employee.employeeId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${employee.previousMonth.consumedDays} days`}
                        size="small"
                        color={employee.previousMonth.consumedDays > 0 ? 'primary' : 'default'}
                        variant={employee.previousMonth.consumedDays > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${employee.currentMonth.consumedDays} days`}
                        size="small"
                        color={employee.currentMonth.consumedDays > 0 ? 'secondary' : 'default'}
                        variant={employee.currentMonth.consumedDays > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${employee.nextMonth.consumedDays} days`}
                        size="small"
                        color={employee.nextMonth.consumedDays > 0 ? 'info' : 'default'}
                        variant={employee.nextMonth.consumedDays > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {reportData && reportData.employees?.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No employees found for the selected period.
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
}
